import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Tooltip,
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { Tabs, Tab } from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import ListAltIcon from "@mui/icons-material/ListAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// Use your client-side service file.
// Adjust this path if your services folder is different.
import FeeStructureUploadService from "../../../services/FeeStructureUpload.service";

/* ============================================================
   CSV TEMPLATE
============================================================ */

const FEE_STRUCTURE_HEADERS = [
  "admittedbatch",
  "program",
  "branch",
  "seat_type",
  "fee_category",
  "fee_type",
  "fee_amount",
  "term1",
  "term2",
  "term3",
  "enroll_type",
  "pay_type",
  "noof_years",
  "which_year",
  "remarks",
  "report_flag",
  "lock_status",
];

const downloadFeeStructureTemplate = () => {
  const sampleRows = [
    [
      "2025-2028",
      "B.Sc",
      "Computer Science",
      "C/M",
      "TUITIONFEE",
      "TUITIONFEE",
      "35000",
      "12000",
      "12000",
      "11000",
      "AUTO/SELF",
      "PART/FULL",
      "3",
      "0",
      "Annual tuition fee",
      "0",
      "0",
    ],
  ];

  const escapeCsvValue = (value) => {
    const text = String(value ?? "");

    if (text.includes(",") || text.includes('"') || text.includes("\n")) {
      return `"${text.replace(/"/g, '""')}"`;
    }

    return text;
  };

  const csvContent = [
    FEE_STRUCTURE_HEADERS.join(","),
    ...sampleRows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\r\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = "FeeStructureUpload_Template.csv";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/* ============================================================
   HELPERS
============================================================ */

const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getResponseData = (response) => {
  if (response?.data?.data !== undefined) {
    return response.data.data;
  }

  if (response?.data !== undefined) {
    return response.data;
  }

  return response;
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

const getRowId = (row, index) => {
  return row?.row_id ?? row?.id ?? row?.__rowNum__ ?? row?.row_number ?? index + 1;
};

const formatAmount = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return value ?? "";
  }

  return amount.toLocaleString("en-IN");
};

/* ============================================================
   GRID TOOLBAR
============================================================ */

const FeeStructureGridToolbar = () => {
  return (
    <GridToolbarContainer
      sx={{
        px: 1,
        py: 1,
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <GridToolbarFilterButton />

      <GridToolbarQuickFilter debounceMs={300} placeholder="Search fee structure..." />
    </GridToolbarContainer>
  );
};
const blueButtonSx = {
  backgroundColor: "#1976D2",
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#1565C0",
    color: "#FFFFFF",
  },
  "&:disabled": {
    backgroundColor: "#90CAF9",
    color: "#FFFFFF",
  },
};
/* ============================================================
   MAIN COMPONENT
============================================================ */

const FeeStructureUpload = () => {
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);

  const [previewRows, setPreviewRows] = useState([]);
  const [validationSummary, setValidationSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  // =========================================================
  // EXISTING FEE STRUCTURE LISTING
  // =========================================================
  const [summaryRows, setSummaryRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);

  const [selectedBatch, setSelectedBatch] = useState(null);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const user = useMemo(() => getSessionUser(), []);

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const userEmail = user?.user_email || user?.email || user?.user_name || "";

  const validRows = useMemo(() => {
    return previewRows.filter((row) => row.valid === true);
  }, [previewRows]);

  const invalidRows = useMemo(() => {
    return previewRows.filter((row) => row.valid !== true);
  }, [previewRows]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /* ============================================================
     FILE SELECTION
  ============================================================ */
  // =========================================================
  // LOAD ADMITTED BATCH SUMMARY
  // =========================================================
  const loadFeeStructureSummary = useCallback(async () => {
    if (!clientId || !collegeId) {
      return;
    }

    setSummaryLoading(true);

    try {
      const response = await FeeStructureUploadService.getAdmittedBatchFeeStructureSummary(
        clientId,
        collegeId
      );

      const result = getResponseData(response);

      const rows = Array.isArray(result?.rows) ? result.rows : [];

      setSummaryRows(
        rows.map((row, index) => ({
          ...row,
          id: row.admittedbatch_id ?? index + 1,
          total_rows: Number(row.total_rows || 0),
        }))
      );
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId, collegeId]);

  // =========================================================
  // LOAD SUMMARY WHEN PAGE OPENS
  // =========================================================
  useEffect(() => {
    loadFeeStructureSummary();
  }, [loadFeeStructureSummary]);

  // =========================================================
  // SHOW FEE STRUCTURE FOR SELECTED BATCH
  // =========================================================
  const handleShowFeeStructure = async (batch) => {
    if (!clientId || !collegeId) {
      showSnackbar("Client ID or College ID is missing from the session.", "error");
      return;
    }

    setSelectedBatch(batch);
    setDetailRows([]);
    setDetailLoading(true);

    try {
      const response = await FeeStructureUploadService.getFeeStructuresByAdmittedBatch(
        clientId,
        collegeId,
        batch.admittedbatch_id
      );

      const result = getResponseData(response);

      const rows = Array.isArray(result?.rows) ? result.rows : [];

      setDetailRows(
        rows.map((row, index) => ({
          ...row,
          id: row.fee_structure_id ?? index + 1,
        }))
      );

      if (!rows.length) {
        showSnackbar("No fee structure records found for this admitted batch.", "warning");
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setDetailLoading(false);
    }
  };

  // =========================================================
  // DOWNLOAD PDF
  // =========================================================
  const handleDownloadFeeStructurePdf = async () => {
    if (!selectedBatch) {
      showSnackbar("Please select an admitted batch.", "warning");
      return;
    }

    setPdfLoading(true);

    try {
      const response = await FeeStructureUploadService.downloadFeeStructurePdf(
        clientId,
        collegeId,
        selectedBatch.admittedbatch_id
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      const batchName = String(selectedBatch.admittedbatch || "FeeStructure").replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      );

      link.download = `College_${collegeId}_FeeStructure_${batchName}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      showSnackbar("Fee structure PDF downloaded successfully.", "success");
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setPdfLoading(false);
    }
  };
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();

    const allowedExtensions = ["csv", "xlsx", "xls"];

    if (!allowedExtensions.includes(extension)) {
      showSnackbar("Please select a CSV, XLS, or XLSX file.", "warning");

      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setPreviewRows([]);
    setValidationSummary(null);
  };

  /* ============================================================
     CLEAR FILE
  ============================================================ */

  const handleClearFile = () => {
    setSelectedFile(null);
    setPreviewRows([]);
    setValidationSummary(null);
    setPage(0);
    setPageSize(10);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* ============================================================
     VALIDATE / PREVIEW
  ============================================================ */

  const handleValidate = async () => {
    if (!selectedFile) {
      showSnackbar("Please select a fee structure file.", "warning");
      return;
    }

    if (!clientId || !collegeId) {
      showSnackbar("Client ID or College ID is missing from the session.", "error");
      return;
    }

    setLoading(true);
    setPreviewRows([]);
    setValidationSummary(null);

    setPage(0);
    setPageSize(10);

    try {
      const response = await FeeStructureUploadService.validateFeeStructureUpload(
        selectedFile,
        clientId,
        collegeId
      );

      const result = getResponseData(response);

      const rows = Array.isArray(result?.rows)
        ? result.rows
        : Array.isArray(result?.data)
        ? result.data
        : [];

      const normalizedRows = rows.map((row, index) => ({
        ...row,
        id: getRowId(row, index),
        row_number: row.row_number ?? row.rowNumber ?? index + 2,
        errors: Array.isArray(row.errors) ? row.errors : row.errors ? [String(row.errors)] : [],
        valid: row.valid === true,
      }));

      setPreviewRows(normalizedRows);

      const validCount = normalizedRows.filter((row) => row.valid === true).length;

      const invalidCount = normalizedRows.length - validCount;

      setValidationSummary({
        total: result?.summary?.total ?? result?.total ?? normalizedRows.length,

        valid: result?.summary?.valid ?? result?.valid ?? validCount,

        invalid: result?.summary?.invalid ?? result?.invalid ?? invalidCount,
      });

      if (normalizedRows.length === 0) {
        showSnackbar("No rows were found in the selected file.", "warning");
      } else if (invalidCount > 0) {
        showSnackbar(
          `Validation completed. ${validCount} valid and ${invalidCount} invalid rows found.`,
          "warning"
        );
      } else {
        showSnackbar(`Validation completed. All ${validCount} rows are valid.`, "success");
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     IMPORT
  ============================================================ */

  const handleImport = async () => {
    if (!validRows.length) {
      showSnackbar("There are no valid rows to import.", "warning");
      return;
    }

    if (!clientId || !collegeId) {
      showSnackbar("Client ID or College ID is missing from the session.", "error");
      return;
    }

    setImporting(true);

    try {
      /*
       * Send only rows that passed validation.
       *
       * The backend must still validate the rows and resolve
       * master IDs independently. Do not trust frontend validity.
       */

      const rowsToImport = validRows.map((row) => {
        const { id, row_number, errors, ...payload } = row;

        return payload;
      });

      const response = await FeeStructureUploadService.importFeeStructureUpload({
        client_id: clientId,
        college_id: collegeId,
        created_by: userEmail,
        rows: rowsToImport,
      });

      const result = getResponseData(response);

      const insertedCount =
        result?.inserted ?? result?.insertedCount ?? result?.count ?? rowsToImport.length;

      showSnackbar(`Fee structure import completed. ${insertedCount} records imported.`, "success");

      // Reset preview after successful import.
      setPreviewRows([]);
      setValidationSummary(null);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setImporting(false);
    }
  };

  // =========================================================
  // ADMITTED BATCH SUMMARY GRID COLUMNS
  // =========================================================
  const summaryColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        width: 80,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          summaryRows.findIndex((row) => row.admittedbatch_id === params.row.admittedbatch_id) + 1,
      },
      {
        field: "admittedbatch",
        headerName: "Admitted Batch",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "total_rows",
        headerName: "Number of Fee Structures",
        width: 200,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "action",
        headerName: "Action",
        width: 190,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            sx={blueButtonSx}
            startIcon={<VisibilityIcon />}
            onClick={() => handleShowFeeStructure(params.row)}
            disabled={detailLoading}
          >
            Show FeeStructure
          </Button>
        ),
      },
    ],
    [summaryRows, detailLoading]
  );

  // =========================================================
  // FEE STRUCTURE DETAIL GRID COLUMNS
  // =========================================================
  const detailColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        width: 75,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          detailRows.findIndex((row) => row.fee_structure_id === params.row.fee_structure_id) + 1,
      },
      {
        field: "program_name",
        headerName: "Program",
        minWidth: 130,
        flex: 0.8,
      },
      {
        field: "branch_name",
        headerName: "Branch",
        minWidth: 160,
        flex: 1,
      },
      {
        field: "seat_type",
        headerName: "Seat Type",
        width: 100,
      },
      {
        field: "fee_category_name",
        headerName: "Fee Category",
        minWidth: 140,
        flex: 0.9,
      },
      {
        field: "fee_type_name",
        headerName: "Fee Type",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "fee_amount",
        headerName: "Fee Amount",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "term1",
        headerName: "Term 1",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "term2",
        headerName: "Term 2",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "term3",
        headerName: "Term 3",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "enroll_type",
        headerName: "Enroll Type",
        width: 110,
      },
      {
        field: "pay_type",
        headerName: "Pay Type",
        width: 110,
      },
      {
        field: "noof_years",
        headerName: "No. of Years",
        width: 110,
      },
      {
        field: "which_year",
        headerName: "Which Year",
        width: 110,
      },
      {
        field: "report_flag",
        headerName: "Report Flag",
        width: 110,
      },
      {
        field: "assign_status",
        headerName: "Assign Status",
        width: 120,
      },
      {
        field: "lock_status",
        headerName: "Lock Status",
        width: 110,
      },
      {
        field: "remarks",
        headerName: "Remarks",
        minWidth: 160,
        flex: 1,
      },
    ],
    [detailRows]
  );
  /* ============================================================
     GRID COLUMNS
  ============================================================ */

  const columns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "S. No.",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return params.api.getRowIndex(params.id) + 1;
        },
      },
      {
        field: "row_number",
        headerName: "Row",
        width: 75,
        sortable: true,
      },
      {
        field: "admittedbatch_name",
        headerName: "Admitted Batch",
        minWidth: 130,
        flex: 0.8,
      },
      {
        field: "program_name",
        headerName: "Program",
        minWidth: 130,
        flex: 1,
      },
      {
        field: "branch_name",
        headerName: "Branch",
        minWidth: 160,
        flex: 1.2,
      },
      {
        field: "seat_type",
        headerName: "Seat Type",
        width: 100,
      },
      {
        field: "fee_category_name",
        headerName: "Fee Category",
        minWidth: 130,
        flex: 0.8,
      },
      {
        field: "fee_type_name",
        headerName: "Fee Type",
        minWidth: 160,
        flex: 1,
      },
      {
        field: "fee_amount",
        headerName: "Fee Amount",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },

      {
        field: "term1",
        headerName: "Term 1",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "term2",
        headerName: "Term 2",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "term3",
        headerName: "Term 3",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "remarks",
        headerName: "Remarks",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "validation_status",
        headerName: "Validation",
        width: 130,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;

          if (row.valid) {
            return (
              <Chip
                icon={<CheckCircleIcon />}
                label="Valid"
                color="success"
                size="small"
                variant="outlined"
              />
            );
          }

          return (
            <Tooltip title={row.errors?.length ? row.errors.join("; ") : "Invalid row"} arrow>
              <Chip
                icon={<ErrorIcon />}
                label="Invalid"
                color="error"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          );
        },
      },
      {
        field: "errors",
        headerName: "Validation Errors",
        minWidth: 250,
        flex: 1.5,
        sortable: false,
        renderCell: (params) => {
          const errors = params.value || [];

          if (!errors.length) {
            return (
              <Typography variant="body2" color="success.main">
                —
              </Typography>
            );
          }

          return (
            <Tooltip title={errors.join("; ")} arrow placement="top">
              <Typography
                variant="body2"
                color="error.main"
                sx={{
                  whiteSpace: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxHeight: 58,
                }}
              >
                {errors.join("; ")}
              </Typography>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <Box sx={{ width: "100%" }}>
      {/* Tabs */},
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2, ml: 3, mt: 0 }}
      >
        <Grid item xs={12} md={7}>
          <Typography variant="h5" fontWeight={700} color="primary">
            UPLOAD FEE STRUCTURE
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Upload fee structures for admitted batches, programs, branches, seat types, fee
            categories, and fee types.
          </Typography>
        </Grid>
      </Grid>
      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 66,
            mb: 2,
            borderBottom: "1px solid #e0e0e0",

            "& .MuiTabs-indicator": {
              height: 4,
              borderRadius: "4px 4px 0 0",
              backgroundColor: "#455a64",
            },

            "& .MuiTab-root": {
              minHeight: 60,
              minWidth: { xs: 155, sm: 220 },
              px: 2.5,
              py: 1.5,
              mr: 1,
              borderRadius: "8px 8px 0 0",
              fontWeight: 700,
              fontSize: { xs: 13, sm: 15 },
              textTransform: "none",
              opacity: 1,
              transition: "background-color 0.2s ease",
            },

            /* Pending - Light Orange */
            "& .MuiTab-root:nth-of-type(1)": {
              backgroundColor: "#FFF3E0",
              color: "#9A5300",
              "&:hover": {
                backgroundColor: "#FFE0B2",
              },
            },

            /* Approved - Light Green */
            "& .MuiTab-root:nth-of-type(2)": {
              backgroundColor: "#E8F5E9",
              color: "#1B5E20",
              "&:hover": {
                backgroundColor: "#C8E6C9",
              },
            },

            "& .MuiTab-root.Mui-selected": {
              fontWeight: 800,
              boxShadow: "inset 0 -4px 0 #455a64",
            },

            "& .MuiTabs-scrollButtons": {
              color: "#1976d2",
            },
          }}
        >
          <Tab label="Fee Structure Summary" icon={<ListAltIcon />} iconPosition="start" />

          <Tab label="Upload Fee Structure" icon={<UploadFileIcon />} iconPosition="start" />
        </Tabs>
      </Card>
      {/* TAB 1: Fee Structure Summary */}
      {activeTab === 0 && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {/* =====================================================
        ADMITTED BATCH FEE STRUCTURE SUMMARY
    ====================================================== */}

          <Card
            elevation={3}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="h5" fontWeight={700} color="primary">
                  ADMITTED BATCH FEE STRUCTURE
                </Typography>

                <Button
                  variant="outlined"
                  sx={blueButtonSx}
                  onClick={loadFeeStructureSummary}
                  disabled={summaryLoading}
                >
                  {summaryLoading ? "Loading..." : "Refresh"}
                </Button>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ width: "100%", overflow: "hidden" }}>
                <DataGrid
                  rows={summaryRows}
                  columns={summaryColumns}
                  getRowId={(row) => row.admittedbatch_id}
                  loading={summaryLoading}
                  autoHeight
                  disableRowSelectionOnClick
                  pagination
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        page: 0,
                        pageSize: 10,
                      },
                    },
                  }}
                  slots={{
                    toolbar: FeeStructureGridToolbar,
                  }}
                  sx={{
                    minHeight: 250,

                    "& .MuiDataGrid-cell": {
                      alignItems: "center",
                    },

                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#E3F2FD",
                      fontWeight: 700,
                    },

                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#F5F9FF",
                    },

                    "& .MuiDataGrid-footerContainer": {
                      backgroundColor: "#F5F9FF",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* =====================================================
        SELECTED ADMITTED BATCH FEE STRUCTURE DETAILS
    ====================================================== */}

          {/*    {selectedBatch && (
            <Card
              elevation={3}
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="primary">
                      FEE STRUCTURE DETAILS
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Admitted Batch: {selectedBatch.admittedbatch}
                      {" | "}
                      Total Records: {detailRows.length}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={blueButtonSx}
                      startIcon={
                        pdfLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <PictureAsPdfIcon />
                        )
                      }
                      onClick={handleDownloadFeeStructurePdf}
                      disabled={pdfLoading || detailLoading || detailRows.length === 0}
                    >
                      {pdfLoading ? "Generating PDF..." : "Download FeeStructure"}
                    </Button>

                    <Button
                      variant="outlined"
                      sx={blueButtonSx}
                      onClick={() => {
                        setSelectedBatch(null);
                        setDetailRows([]);
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ width: "100%", overflow: "hidden" }}>
                  <DataGrid
                    rows={detailRows}
                    columns={detailColumns}
                    getRowId={(row) => row.id}
                    loading={detailLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pagination
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          page: 0,
                          pageSize: 10,
                        },
                      },
                    }}
                    slots={{
                      toolbar: FeeStructureGridToolbar,
                    }}
                    getRowHeight={() => "auto"}
                    sx={{
                      minHeight: 350,

                      "& .MuiDataGrid-cell": {
                        whiteSpace: "normal",
                        lineHeight: "1.4rem",
                        py: 1,
                        alignItems: "center",
                      },

                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#E3F2FD",
                        fontWeight: 700,
                      },

                      "& .MuiDataGrid-row": {
                        minHeight: "52px !important",
                      },

                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#F5F9FF",
                      },

                      "& .MuiDataGrid-footerContainer": {
                        backgroundColor: "#F5F9FF",
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          )} */}
        </Box>
      )}
      {/* =====================================================
        ORIGINAL UPLOAD FEE STRUCTURE SECTION
        KEEPING YOUR EXISTING CODE
    ====================================================== */}
      {activeTab === 1 && (
        <Box>
          <Card
            elevation={3}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <CardContent>
              {/* HEADER */}

              <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                <Grid item xs={12} md={7}></Grid>

                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={downloadFeeStructureTemplate}
                    sx={blueButtonSx}
                  >
                    Download CSV Template
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* FILE SELECTION */}

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      hidden
                      onChange={handleFileSelect}
                    />

                    <Button
                      variant="contained"
                      sx={blueButtonSx}
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading || importing}
                    >
                      Select Excel/CSV File
                    </Button>

                    {selectedFile && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedFile.name}
                        </Typography>

                        <Tooltip title="Remove selected file">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={handleClearFile}
                            disabled={loading || importing}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    sx={blueButtonSx}
                    onClick={handleValidate}
                    disabled={!selectedFile || loading || importing}
                    startIcon={
                      loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />
                    }
                  >
                    {loading ? "Validating..." : "Validate & Preview"}
                  </Button>
                </Grid>
              </Grid>

              {/* TEMPLATE INFORMATION */}

              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Important:</strong> Branch is mandatory. Use either master names or IDs
                  for admitted batch, program, branch, fee category, and fee type. Fee type must
                  belong to the selected fee category. The backend should verify the batch/program
                  and batch/program/branch mappings before importing.
                </Typography>
              </Box>

              {/* VALIDATION SUMMARY */}

              {/* IMPORT BUTTON */}

              {previewRows.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearFile}
                    disabled={importing}
                    sx={blueButtonSx}
                  >
                    Clear
                  </Button>

                  <Button
                    variant="contained"
                    sx={blueButtonSx}
                    startIcon={
                      importing ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                    }
                    onClick={handleImport}
                    disabled={importing || loading || validRows.length === 0}
                  >
                    {importing ? "Importing..." : `Import Valid Rows (${validRows.length})`}
                  </Button>
                </Box>
              )}
            </CardContent>

            {/* PREVIEW GRID */}

            {previewRows.length > 0 && (
              <Card
                elevation={3}
                sx={{
                  mt: 0,
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0,
                      mb: 0,
                    }}
                  >
                    <Typography variant="h5" fontWeight={700}>
                      PREVIEW FEE STRUCTURE
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 0,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip label={`Valid: ${validRows.length}`} color="success" size="small" />

                      <Chip label={`Invalid: ${invalidRows.length}`} color="error" size="small" />
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  <Box
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <DataGrid
                      rows={previewRows}
                      columns={columns}
                      getRowId={(row) => row.id}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      paginationMode="client"
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={{
                        page,
                        pageSize,
                      }}
                      onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                      }}
                      slots={{
                        toolbar: FeeStructureGridToolbar,
                      }}
                      getRowHeight={() => "auto"}
                      sx={{
                        minHeight: 400,

                        "& .MuiDataGrid-cell": {
                          whiteSpace: "normal",
                          lineHeight: "1.4rem",
                          py: 1,
                          alignItems: "center",
                        },

                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#E3F2FD",
                          fontWeight: 700,
                        },

                        "& .MuiDataGrid-row": {
                          minHeight: "52px !important",
                        },

                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "#F5F9FF",
                        },

                        "& .MuiDataGrid-footerContainer": {
                          backgroundColor: "#F5F9FF",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Card>
        </Box>
      )}
      {/* =====================================================
    FEE STRUCTURE DETAILS DIALOG
===================================================== */}
      <Dialog
        open={Boolean(selectedBatch)}
        onClose={() => {
          setSelectedBatch(null);
          setDetailRows([]);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "92vh",
          },
        }}
      >
        {/* DIALOG HEADER */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#E3F2FD",
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary">
              FEE STRUCTURE DETAILS
            </Typography>

            {selectedBatch && (
              <Typography variant="body2" color="text.secondary">
                Admitted Batch: {selectedBatch.admittedbatch}
                {" | "}
                Total Records: {detailRows.length}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={() => {
              setSelectedBatch(null);
              setDetailRows([]);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        {/* DIALOG CONTENT */}
        <DialogContent sx={{ p: 2 }}>
          {/* PDF DOWNLOAD BUTTON */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              sx={blueButtonSx}
              startIcon={
                pdfLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />
              }
              onClick={handleDownloadFeeStructurePdf}
              disabled={pdfLoading || detailLoading || detailRows.length === 0}
            >
              {pdfLoading ? "Generating PDF..." : "Download FeeStructure"}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* FEE STRUCTURE DETAILS GRID */}
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <DataGrid
              rows={detailRows}
              columns={detailColumns}
              getRowId={(row) => row.id}
              loading={detailLoading}
              autoHeight
              disableRowSelectionOnClick
              pagination
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              slots={{
                toolbar: FeeStructureGridToolbar,
              }}
              getRowHeight={() => "auto"}
              sx={{
                minHeight: 350,

                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  lineHeight: "1.4rem",
                  py: 1,
                  alignItems: "center",
                },

                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#E3F2FD",
                  fontWeight: 700,
                },

                "& .MuiDataGrid-row": {
                  minHeight: "52px !important",
                },

                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#F5F9FF",
                },

                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#F5F9FF",
                },
              }}
            />
          </Box>
        </DialogContent>

        {/* DIALOG FOOTER */}
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            variant="outlined"
            sx={blueButtonSx}
            onClick={() => {
              setSelectedBatch(null);
              setDetailRows([]);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeStructureUpload;
